import * as React from 'react';

import { cn, ConsoleTouchDialog, useDebouncedValue } from '@rematter/ui-web';
import { useAtomValue } from 'jotai';
import MiniSearch from 'minisearch';

import { HighlightedMaterialsSection } from './HighlightedMaterialsSection';
import { InventoryItemsModalContent } from './InventoryItemsModalContent';
import { MaterialListHeader } from './InventoryItemsModalHeader';
import { InventoryTagOption } from './InventoryItemsModalTagsGrid';

import { $inventoryItems } from '~/modules/trade/_state/loadForm.state';
import { $scaleTicketQuickAddGroupIds } from '~/modules/trade/_state/scaleTicketSettings.state';
import { useInventoryTags } from '~/modules/trade/hooks/useInventoryTags';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { InventoryItemRecord } from '~/modules/trade/types/load';
import { useTheme } from '~/shared/hooks';

export type InventoryItemsModalMode = 'items' | 'tags';

export type InventoryTagData = {
  id: string;
  tagNumber: string;
  netQuantity: number;
  grossQuantity: number;
  tare: number;
  item: InventoryItemRecord;
};

const createInventoryMiniSearch = () =>
  new MiniSearch({
    fields: ['itemGroupName', 'itemName', 'mnemonic'],
    storeFields: ['id'],
    searchOptions: {
      prefix: true,
      boost: { mnemonic: 2 },
      fuzzy: 0.2,
    },
  });

const createTagsMiniSearch = () =>
  new MiniSearch({
    fields: ['itemName', 'tagNumber'],
    storeFields: ['id'],
    searchOptions: {
      prefix: true,
      boost: { tagNumber: 2 },
      fuzzy: 0.2,
    },
  });

const EMPTY_ARRAY: never[] = [];

type InventoryItemsModalProps = {
  isOpen: boolean;
  mode?: InventoryItemsModalMode;
  onSelectMaterial: (materialId: string) => void;
  onSelectInventoryTag?: (
    inventoryTagId: string,
    tagData: InventoryTagData,
  ) => void;
  onOpenChange: (isOpen: boolean) => void;
  modalStyle?: React.CSSProperties;
  className?: string;
  initialSelectedItemId?: string | null;
  initialItemId?: string | null;
  highlightedMaterialIds?: string[];
  highlightedSectionTitle?: string;
};

export const InventoryItemsModal = ({
  isOpen,
  mode = 'items',
  onOpenChange,
  onSelectMaterial,
  onSelectInventoryTag,
  modalStyle,
  className,
  initialSelectedItemId,
  initialItemId,
  highlightedMaterialIds,
  highlightedSectionTitle,
}: InventoryItemsModalProps) => {
  const t = useTranslations();
  const { effectiveColorScheme } = useTheme();
  const { data: itemsData } = useAtomValue($inventoryItems);
  const quickAddGroupIds = useAtomValue($scaleTicketQuickAddGroupIds);
  const { inventoryTagsOptions, inventoryTagOptionsByItemId } =
    useInventoryTags();

  const [searchTerm, setSearchTerm] = React.useState('');
  const [selectedGroupId, setSelectedGroupId] = React.useState<string | null>(
    null,
  );
  const [selectedItemId, setSelectedItemId] = React.useState<string | null>(
    null,
  );
  const searchInputRef = React.useRef<HTMLDivElement>(null);

  const isTagsMode = mode === 'tags';

  React.useEffect(() => {
    if (!isOpen) {
      setSelectedGroupId(null);
      setSelectedItemId(null);
      setSearchTerm('');
      return;
    }

    if (isTagsMode && initialItemId) {
      const item = itemsData?.itemsById?.[initialItemId];
      if (item?.itemGroupId) {
        setSelectedGroupId(item.itemGroupId);
        setSelectedItemId(initialItemId);
      }
      return;
    }

    if (!initialSelectedItemId) return;

    if (isTagsMode) {
      const tag = inventoryTagsOptions.find(
        (t) => t.id === initialSelectedItemId,
      );
      const groupId = tag?.tagData?.item?.itemGroup?.id;
      if (groupId) {
        setSelectedGroupId(groupId);
      }
    } else {
      const item = itemsData?.itemsById?.[initialSelectedItemId];
      if (item?.itemGroupId) {
        setSelectedGroupId(item.itemGroupId);
      }
    }
  }, [
    isOpen,
    initialSelectedItemId,
    initialItemId,
    isTagsMode,
    itemsData?.itemsById,
    inventoryTagsOptions,
  ]);

  const inventoryItems = itemsData?.allItems ?? EMPTY_ARRAY;

  const highlightedInventoryItems = React.useMemo<InventoryItemRecord[]>(() => {
    if (!highlightedMaterialIds || !itemsData?.itemsById) return EMPTY_ARRAY;

    return highlightedMaterialIds
      .map((id) => itemsData.itemsById[id])
      .filter((item): item is InventoryItemRecord => item != null);
  }, [highlightedMaterialIds, itemsData?.itemsById]);

  const itemGroups = React.useMemo(() => {
    if (!itemsData?.itemGroups) return EMPTY_ARRAY;

    const itemsByGroupId = new Map(
      inventoryItems.map((item) => [item.itemGroupId, item]),
    );
    const allGroups = itemsData.itemGroups.map((group) => {
      const sampleItem = itemsByGroupId.get(group?.id ?? '');
      const groupColorHue = (
        sampleItem?.metadata as { itemGroup?: { colorHue?: number | null } }
      )?.itemGroup?.colorHue;
      return {
        id: group?.id ?? '',
        name: group?.name ?? '',
        itemGroupId: group?.id ?? '',
        colorHue: groupColorHue,
        metadata: { itemGroup: { colorHue: groupColorHue } },
      };
    });

    if (isTagsMode) {
      const groupIdsWithTags = new Set(
        inventoryTagsOptions
          .map((tag) => tag.tagData?.item?.itemGroup?.id)
          .filter(Boolean),
      );
      return allGroups.filter((group) => groupIdsWithTags.has(group.id));
    }

    if (quickAddGroupIds.length > 0) {
      const quickSet = new Set(quickAddGroupIds);
      const prioritized = allGroups.filter((g) => quickSet.has(g.id));
      const rest = allGroups.filter((g) => !quickSet.has(g.id));
      return [...prioritized, ...rest];
    }

    return allGroups;
  }, [
    itemsData?.itemGroups,
    inventoryItems,
    isTagsMode,
    inventoryTagsOptions,
    quickAddGroupIds,
  ]);

  const materialsSearchIndex = React.useMemo(() => {
    const miniSearch = createInventoryMiniSearch();
    const inventoryItemsToSearch = inventoryItems.map((item) => ({
      id: item.id,
      itemName: item.name,
      itemGroupName: item.itemGroup?.name,
      mnemonic: item.mnemonic,
    }));
    miniSearch.addAll(inventoryItemsToSearch);
    return miniSearch;
  }, [inventoryItems]);

  const tagsSearchIndex = React.useMemo(() => {
    const miniSearch = createTagsMiniSearch();
    const tagsForSearch = inventoryTagsOptions.map((tag) => ({
      id: tag.id,
      itemName: tag.tagData?.item?.name ?? '',
      tagNumber: tag.tagData?.tagNumber ?? '',
    }));
    miniSearch.addAll(tagsForSearch);
    return miniSearch;
  }, [inventoryTagsOptions]);

  const materialSearchResults = React.useMemo<InventoryItemRecord[]>(() => {
    if (!searchTerm) return EMPTY_ARRAY;

    const searchResults = materialsSearchIndex.search(searchTerm);
    const itemsById = new Map(inventoryItems.map((item) => [item.id, item]));

    return searchResults
      .map((result) => itemsById.get(result.id))
      .filter((item): item is InventoryItemRecord => item != null);
  }, [searchTerm, materialsSearchIndex, inventoryItems]);

  const groupSearchResults = React.useMemo(() => {
    const uniqueGroupIds = new Set(
      materialSearchResults.map((item) => item.itemGroupId).filter(Boolean),
    );

    return itemGroups.filter((group) => uniqueGroupIds.has(group.id));
  }, [materialSearchResults, itemGroups]);

  const tagSearchResults = React.useMemo<InventoryTagOption[]>(() => {
    if (!searchTerm || !isTagsMode) return EMPTY_ARRAY;

    const searchResults = tagsSearchIndex.search(searchTerm);
    const tagsById = new Map(inventoryTagsOptions.map((tag) => [tag.id, tag]));

    return searchResults
      .map((result) => tagsById.get(result.id))
      .filter((tag) => tag != null);
  }, [searchTerm, isTagsMode, tagsSearchIndex, inventoryTagsOptions]);

  const tagGroupSearchResults = React.useMemo(() => {
    if (!isTagsMode) return EMPTY_ARRAY;

    const uniqueGroupIds = new Set(
      tagSearchResults
        .map((tag) => tag.tagData?.item?.itemGroup?.id)
        .filter(Boolean),
    );

    return itemGroups.filter((group) => uniqueGroupIds.has(group.id));
  }, [tagSearchResults, itemGroups, isTagsMode]);

  const tagsForSelectedGroup = React.useMemo<InventoryTagOption[]>(() => {
    if (!selectedGroupId || !isTagsMode) return EMPTY_ARRAY;

    return (inventoryTagsOptions as InventoryTagOption[])
      .filter((tag) => tag.tagData?.item?.itemGroup?.id === selectedGroupId)
      .sort((a, b) => {
        const nameA = a.tagData?.item?.name ?? '';
        const nameB = b.tagData?.item?.name ?? '';
        return nameA.localeCompare(nameB);
      });
  }, [selectedGroupId, isTagsMode, inventoryTagsOptions]);

  const tagsForSelectedItem = React.useMemo<InventoryTagOption[]>(() => {
    if (!selectedItemId || !isTagsMode) return EMPTY_ARRAY;
    return (
      (inventoryTagOptionsByItemId.get(
        selectedItemId,
      ) as InventoryTagOption[]) ?? EMPTY_ARRAY
    );
  }, [selectedItemId, isTagsMode, inventoryTagOptionsByItemId]);

  const hasNoResults = useDebouncedValue(
    searchTerm.length > 0 &&
      (isTagsMode
        ? tagSearchResults.length === 0
        : materialSearchResults.length === 0),
    250,
  );

  const selectedItemGroup = itemGroups.find(
    (group) => group.id === selectedGroupId,
  );

  const handleNavigateUp = () => {
    if (isTagsMode && selectedItemId) {
      setSelectedItemId(null);
    } else if (selectedGroupId) {
      setSelectedGroupId(null);
    } else {
      onOpenChange(false);
    }
  };

  const handleSelectGroup = (groupId: string) => {
    setSelectedGroupId(groupId);
    setSelectedItemId(null);
    setSearchTerm('');
  };

  const handleSelectItem = (itemId: string) => {
    setSelectedItemId(itemId);
  };

  const handleSelectMaterial = (materialId: string) => {
    onSelectMaterial(materialId);
    onOpenChange(false);
  };

  const handleSelectTag = (tagId: string, tagData: InventoryTagData) => {
    onSelectInventoryTag?.(tagId, tagData);
    onOpenChange(false);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  return (
    <ConsoleTouchDialog
      touchModeEnabled={true}
      effectiveColorScheme={effectiveColorScheme}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      className="@InventoryItemsModal"
      dialogClassName={cn('h-full w-full max-w-full', className)}
      classNameHeader="pb-0"
      contentStyle={modalStyle}
      header={
        <MaterialListHeader
          searchInputRef={searchInputRef}
          selectedMaterialGroupId={selectedGroupId}
          selectedItemId={selectedItemId}
          onNavigateUp={handleNavigateUp}
          searchTerm={searchTerm}
          onChangeInput={setSearchTerm}
          clearSearchTerm={handleClearSearch}
          onSelectMaterialGroup={handleSelectGroup}
          onSelectItem={handleSelectItem}
          itemGroups={itemGroups}
          inventoryItems={inventoryItems}
          inventoryTagOptionsByItemId={inventoryTagOptionsByItemId}
          autoFocus={isOpen && !selectedGroupId}
          mode={mode}
        />
      }
    >
      {/* 🟡🟡🟡 HIGHLIGHTED MATERIALS SECTION */}
      {highlightedInventoryItems.length > 0 &&
        highlightedSectionTitle &&
        !selectedGroupId && (
          <>
            <HighlightedMaterialsSection
              materials={highlightedInventoryItems}
              sectionTitle={highlightedSectionTitle}
              onSelectMaterial={handleSelectMaterial}
            />
            <div className="px-4 pb-2 text-xl font-semibold">
              {t.scaleTicketPro.inventoryItemField.allMaterials}
            </div>
          </>
        )}
      <InventoryItemsModalContent
        mode={mode}
        effectiveColorScheme={effectiveColorScheme}
        searchTerm={searchTerm}
        noResultsFound={hasNoResults}
        selectedMaterialGroupId={selectedGroupId}
        selectedItemId={selectedItemId}
        selectedGroupName={selectedItemGroup?.name ?? ''}
        inventoryItems={inventoryItems}
        materialSearchResults={materialSearchResults}
        groupSearchResults={groupSearchResults}
        tagSearchResults={tagSearchResults}
        tagGroupSearchResults={tagGroupSearchResults}
        tagsForSelectedGroup={tagsForSelectedGroup}
        tagsForSelectedItem={tagsForSelectedItem}
        itemGroups={itemGroups}
        inventoryTagOptionsByItemId={inventoryTagOptionsByItemId}
        highlightedMaterialIds={highlightedMaterialIds}
        highlightedSectionTitle={highlightedSectionTitle}
        selectedId={initialSelectedItemId}
        onSelectGroup={handleSelectGroup}
        onSelectMaterial={handleSelectMaterial}
        onSelectItem={handleSelectItem}
        onSelectTag={handleSelectTag}
      />
    </ConsoleTouchDialog>
  );
};
